/* ==========================================================================
   OEDEMA — theme.js
   Vanilla, dependency-free. Cart drawer (Section Rendering API), product form,
   gallery, quick-add, overlays (focus trap), qty, countdown, mobile nav.
   Event delegation is used wherever possible so handlers survive DOM swaps.
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.oeRoutes || {};
  var strings = window.oeStrings || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function money(cents) {
    // Fallback formatter; server-rendered prices are the source of truth.
    try { return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR' }); }
    catch (e) { return (cents / 100).toFixed(2); }
  }

  /* --- Overlay manager (scrim, focus trap, scroll lock) ------------------- */
  var Overlay = (function () {
    var openEl = null, lastFocus = null, scrim = null;
    function focusables(el) {
      return qsa('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
        .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !openEl) return;
      var f = focusables(openEl); if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function open(el) {
      if (!el) return;
      // Single active overlay: close any prior one first so the shared scrim /
      // scroll-lock never desync when overlays open in sequence.
      var newTrigger = document.activeElement;
      if (openEl && openEl !== el) { close(); }
      scrim = qs('[data-scrim]');
      lastFocus = newTrigger;
      openEl = el;
      if (scrim) { scrim.hidden = false; scrim.classList.add('is-active'); }
      document.body.style.overflow = 'hidden';
      el.classList.add('is-open');
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
      document.removeEventListener('keydown', onKey);
      document.addEventListener('keydown', onKey);
      var f = focusables(el); if (f.length) f[0].focus();
    }
    function close() {
      if (!openEl) return;
      openEl.classList.remove('is-open');
      openEl.setAttribute('aria-hidden', 'true');
      var el = openEl; openEl = null;
      window.setTimeout(function () { if (!document.querySelector('.is-open')) { if (el.hasAttribute('data-hide-on-close')) el.setAttribute('hidden', ''); } }, 260);
      if (scrim) { scrim.classList.remove('is-active'); scrim.hidden = true; }
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.hasAttribute && lastFocus.hasAttribute('aria-expanded')) lastFocus.setAttribute('aria-expanded', 'false');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    return { open: open, close: close, isOpen: function () { return !!openEl; } };
  })();

  /* --- Cart --------------------------------------------------------------- */
  var Cart = (function () {
    function drawer() { return qs('[data-cart-drawer]'); }
    function open() { Overlay.open(drawer()); }
    function close() { Overlay.close(); }

    function setBusy(state) {
      var d = drawer(); if (d) d.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    function refresh() {
      // Preserve open state across a Section-Rendering swap: the server markup comes back
      // hidden + aria-hidden=true, so re-bind the Overlay to the fresh node if it was open.
      var d0 = drawer();
      var wasOpen = !!(d0 && d0.classList.contains('is-open'));
      // NOTE: no 'Accept: application/json' header. On /products/* URLs that header makes
      // Shopify return the product JSON object instead of the ?sections= payload, so the
      // drawer silently never updated after a PDP add. The ?sections= param already
      // responds as JSON on its own on every template.
      var url = window.location.pathname + '?sections=cart-drawer,header';
      return fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          swapSection(data['cart-drawer'], '[data-cart-drawer]');
          swapSection(data['header'], '[data-cart-count]');
          if (wasOpen) { Overlay.open(drawer()); }
        });
    }

    function swapSection(html, selector) {
      if (!html) {
        // A section came back empty — e.g. the Section Rendering API returned an
        // unexpected payload. Fail loudly instead of silently leaving stale markup.
        console.warn('[oe-cart] empty section response for ' + selector + ' — drawer not updated');
        return;
      }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var incoming = doc.querySelector(selector);
      var current = qs(selector);
      if (incoming && current) {
        current.replaceWith(incoming);
      } else {
        console.warn('[oe-cart] could not swap ' + selector + ' (incoming: ' + !!incoming + ', current: ' + !!current + ')');
      }
    }

    function add(variantId, quantity, opts) {
      opts = opts || {};
      setBusy(true);
      var body = { items: [{ id: Number(variantId), quantity: quantity || 1 }] };
      if (opts.properties) body.items[0].properties = opts.properties;
      return fetch(routes.cart_add || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      }).then(function () {
        return refresh();
      }).then(function () {
        setBusy(false);
        open();
      }).catch(function (err) {
        setBusy(false);
        announce((err && err.description) || strings.cartError || 'Could not add to cart');
      });
    }

    function change(line, quantity) {
      setBusy(true);
      return fetch(routes.cart_change || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      }).then(function (r) { return r.json(); })
        .then(function () { return refresh(); })
        .then(function () { setBusy(false); })
        .catch(function () { setBusy(false); announce(strings.cartError || 'Cart update failed'); });
    }

    function announce(msg) {
      var region = qs('[data-cart-error]');
      if (region) { region.textContent = msg; }
      else { window.alert(msg); }
    }

    return { open: open, close: close, add: add, change: change, refresh: refresh };
  })();
  window.oeCart = Cart;

  /* --- Mobile nav --------------------------------------------------------- */
  function navDrawer() { return qs('[data-nav-drawer]'); }

  /* --- Delegated clicks --------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-cart-open], [data-cart-close], [data-nav-open], [data-nav-close], [data-quick-add], [data-qty-plus], [data-qty-minus], [data-line-remove], [data-scrim]');
    if (!t) return;

    if (t.hasAttribute('data-cart-open')) {
      // Let it also work as a link to /cart when JS present: open drawer instead.
      if (qs('[data-cart-drawer]')) { e.preventDefault(); Cart.open(); }
      return;
    }
    if (t.hasAttribute('data-cart-close')) { e.preventDefault(); Cart.close(); return; }
    if (t.hasAttribute('data-nav-open')) { e.preventDefault(); t.setAttribute('aria-expanded', 'true'); Overlay.open(navDrawer()); return; }
    if (t.hasAttribute('data-nav-close')) { e.preventDefault(); Overlay.close(); return; }
    if (t.hasAttribute('data-scrim')) { Overlay.close(); return; }

    if (t.hasAttribute('data-quick-add')) {
      e.preventDefault();
      var vid = t.getAttribute('data-variant-id');
      if (vid) { t.setAttribute('aria-busy', 'true'); Cart.add(vid, 1); window.setTimeout(function () { t.removeAttribute('aria-busy'); }, 600); }
      return;
    }
    if (t.hasAttribute('data-qty-plus') || t.hasAttribute('data-qty-minus')) {
      e.preventDefault();
      var row = t.closest('[data-line]'); if (!row) return;
      var line = row.getAttribute('data-line');
      var input = qs('[data-qty-input]', row);
      var val = parseInt(input ? input.value : '1', 10) || 1;
      val = t.hasAttribute('data-qty-plus') ? val + 1 : Math.max(0, val - 1);
      Cart.change(line, val);
      return;
    }
    if (t.hasAttribute('data-line-remove')) {
      e.preventDefault();
      var r = t.closest('[data-line]'); if (r) Cart.change(r.getAttribute('data-line'), 0);
      return;
    }
  });

  // Qty typed directly
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (input) {
      var row = input.closest('[data-line]');
      if (row) Cart.change(row.getAttribute('data-line'), Math.max(0, parseInt(input.value, 10) || 0));
    }
  });

  /* --- Product form (PDP variant/size) ------------------------------------ */
  function initProductForms() {
    qsa('[data-product-form]').forEach(function (form) {
      if (form.__oeBound) return; form.__oeBound = true;
      var priceTarget = qs('[data-price-target]', form) || (form.closest('[data-product-root]') && qs('[data-price-target]', form.closest('[data-product-root]')));
      var atc = qs('[data-atc-target]', form);
      var idInput = qs('input[name="id"]', form);

      function select(input) {
        if (!input) return;
        if (idInput) idInput.value = input.getAttribute('data-variant-id');
        var priceHtml = input.getAttribute('data-price-html');
        if (priceTarget && priceHtml) priceTarget.innerHTML = priceHtml;
        var available = input.getAttribute('data-available') === 'true';
        if (atc) {
          atc.disabled = !available;
          var label = atc.querySelector('[data-atc-label]') || atc;
          if (label) label.textContent = available ? (atc.getAttribute('data-label-available') || label.textContent) : (strings.soldOut || 'sold out');
        }
      }

      form.addEventListener('change', function (e) {
        var input = e.target.closest('[data-variant-input]');
        if (input) select(input);
      });

      form.addEventListener('submit', function (e) {
        if (!qs('[data-cart-drawer]')) return; // allow native POST fallback
        e.preventDefault();
        var id = idInput ? idInput.value : (qs('[data-variant-input]:checked', form) || {}).value;
        var qtyInput = qs('input[name="quantity"]', form);
        Cart.add(id, qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1);
      });

      var checked = qs('[data-variant-input]:checked', form) || qs('[data-variant-input][data-available="true"]', form);
      if (checked) { checked.checked = true; select(checked); }
    });
  }

  /* --- Gallery ------------------------------------------------------------ */
  function initGalleries() {
    qsa('[data-gallery]').forEach(function (g) {
      if (g.__oeBound) return; g.__oeBound = true;
      var main = qs('[data-gallery-main] img', g) || qs('[data-gallery-main]', g);
      var counter = qs('[data-gallery-counter]', g);
      var thumbs = qsa('[data-gallery-thumb]', g);
      function show(idx) {
        var thumb = thumbs[idx]; if (!thumb) return;
        var src = thumb.getAttribute('data-full') || (qs('img', thumb) || {}).src;
        var alt = thumb.getAttribute('data-alt') || '';
        if (main && src) { main.src = src; main.alt = alt; }
        thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
        if (counter) counter.textContent = (idx + 1) + '/' + thumbs.length;
      }
      g.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb) { e.preventDefault(); show(thumbs.indexOf(thumb)); }
      });
      // Swipe on main (mobile)
      bindSwipe(qs('[data-gallery-main]', g), function (dir) {
        var cur = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
        if (cur < 0) cur = 0;
        show(Math.max(0, Math.min(thumbs.length - 1, cur + (dir === 'left' ? 1 : -1))));
      });
    });
  }

  /* --- Card image swipe (mobile 1/2 swap without breaking the link) ------- */
  function initCardSwaps() {
    if (!document.documentElement.classList.contains('js')) return;
    qsa('[data-card-swap]').forEach(function (media) {
      if (media.__oeBound) return; media.__oeBound = true;
      bindSwipe(media, function () {
        var swapped = media.getAttribute('data-swapped') === 'true';
        media.setAttribute('data-swapped', swapped ? 'false' : 'true');
        var counter = qs('[data-card-counter]', media);
        if (counter) counter.textContent = swapped ? '1/2' : '2/2';
      });
    });
  }

  // Distinguish tap (navigate) from horizontal swipe (swap). Only prevents nav on real swipe.
  function bindSwipe(el, onSwipe) {
    if (!el) return;
    var x0 = null, y0 = null, moved = false;
    el.addEventListener('touchstart', function (e) { var t = e.changedTouches[0]; x0 = t.clientX; y0 = t.clientY; moved = false; }, { passive: true });
    el.addEventListener('touchmove', function (e) { if (x0 === null) return; var t = e.changedTouches[0]; if (Math.abs(t.clientX - x0) > 30 && Math.abs(t.clientX - x0) > Math.abs(t.clientY - y0)) moved = true; }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var t = e.changedTouches[0]; var dx = t.clientX - x0;
      if (moved && Math.abs(dx) > 40) { e.preventDefault(); onSwipe(dx < 0 ? 'left' : 'right'); }
      x0 = y0 = null;
    });
  }

  /* --- Accordions: native <details>, nothing needed. Sticky header. ------- */
  function initStickyHeader() {
    var header = qs('[data-sticky-header]');
    if (!header) return;
    var last = 0;
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset;
      header.classList.toggle('is-compact', y > 80);
      last = y;
    }, { passive: true });
  }

  /* --- Countdown ---------------------------------------------------------- */
  function initCountdowns() {
    qsa('[data-countdown]').forEach(function (el) {
      if (el.__oeBound) return; el.__oeBound = true;
      var deadline = new Date(el.getAttribute('data-deadline')).getTime();
      if (isNaN(deadline)) return;
      var d = qs('[data-cd-days]', el), h = qs('[data-cd-hours]', el), m = qs('[data-cd-mins]', el), s = qs('[data-cd-secs]', el);
      function tick() {
        var diff = Math.max(0, deadline - Date.now());
        var days = Math.floor(diff / 864e5);
        var hours = Math.floor((diff % 864e5) / 36e5);
        var mins = Math.floor((diff % 36e5) / 6e4);
        var secs = Math.floor((diff % 6e4) / 1e3);
        if (d) d.textContent = days; if (h) h.textContent = ('0' + hours).slice(-2);
        if (m) m.textContent = ('0' + mins).slice(-2); if (s) s.textContent = ('0' + secs).slice(-2);
        if (diff <= 0) { clearInterval(el.__t); el.setAttribute('data-expired', 'true'); }
      }
      tick(); el.__t = setInterval(tick, 1000);
    });
  }

  /* --- Collection "load more" (append; progressive enhancement) ----------- */
  // The button is a real <a href> to the next page (works with no JS / for SEO).
  // With JS we intercept it, fetch the next page via the Section Rendering API,
  // and append its product cards in place. Editorial banners are intentionally
  // skipped on append — their "appears after product #" positions restart each
  // page, so re-injecting them would repeat the same banners mid-scroll.
  function initLoadMore() {
    if (initLoadMore.__bound) return; initLoadMore.__bound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      e.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;

      var url = btn.getAttribute('href');
      var grid = qs('[data-collection-grid]');
      if (!url || !grid) { if (url) window.location.href = url; return; }

      // Prefer a lightweight section-only render when we know the section id.
      var fetchUrl = url;
      var sectionId = grid.getAttribute('data-section-id');
      if (sectionId) {
        try { var u = new URL(url, window.location.origin); u.searchParams.set('section_id', sectionId); fetchUrl = u.toString(); }
        catch (err) { fetchUrl = url; }
      }

      var moreWrap = btn.closest('[data-collection-more]');
      var restore = btn.textContent;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'loading…';

      fetch(fetchUrl, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-collection-grid]');
          var firstNew = null;
          if (newGrid) {
            qsa('.oe-card', newGrid).forEach(function (card) {
              // .oe-card is a direct grid child; move it into the live grid.
              if (card.parentNode === newGrid) { if (!firstNew) firstNew = card; grid.appendChild(card); }
            });
          }
          // Swap in the fetched more/pager block so the button now targets the
          // following page — or remove it entirely when there are no more pages.
          var newMore = doc.querySelector('[data-collection-more]');
          if (moreWrap) { if (newMore) { moreWrap.replaceWith(newMore); } else { moreWrap.remove(); } }
          initCardSwaps(); // bind hover/swipe on the appended cards (idempotent)
          // Send keyboard focus to the first newly loaded card.
          if (firstNew) { var link = qs('a', firstNew); if (link) { link.setAttribute('tabindex', '-1'); link.focus(); } }
        })
        .catch(function () { window.location.href = url; }); // graceful fallback
    });
  }

  /* --- Announcement ticker ------------------------------------------------ */
  // The marquee is a two-identical-track loop that slides -50% (one track).
  // It only feels infinite if a single track is at least viewport-wide — with
  // few/short messages on a wide (full-bleed) screen it isn't, so the loop
  // resets with a visible gap. Repeat each track's items until it overflows the
  // viewport, and stretch the duration to match so scroll speed stays constant.
  function initTicker() {
    var roots = qsa('[data-ticker]');
    if (!roots.length) return;

    function fill(root) {
      var ticker = qs('.oe-ticker', root);
      var tracks = qsa('.oe-ticker__track', root);
      if (!ticker || !tracks.length) return;
      var repeats = 1;
      tracks.forEach(function (track, i) {
        if (!track.__orig) track.__orig = Array.prototype.slice.call(track.children);
        // reset to the original set before re-measuring (handles resize)
        while (track.children.length > track.__orig.length) track.removeChild(track.lastChild);
        var base = track.scrollWidth;
        if (!base) return;
        // how many copies of the set it takes to exceed the viewport
        if (i === 0) repeats = Math.max(1, Math.ceil((window.innerWidth + 80) / base));
        for (var r = 1; r < repeats; r++) {
          track.__orig.forEach(function (n) {
            var c = n.cloneNode(true);
            c.setAttribute('aria-hidden', 'true'); // don't re-announce copies
            track.appendChild(c);
          });
        }
      });
      // one set always takes 28s to travel — keeps the original pace at any width
      ticker.style.animationDuration = (28 * repeats) + 's';
    }

    roots.forEach(fill);
    if (!initTicker.__resize) {
      initTicker.__resize = true;
      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(function () { qsa('[data-ticker]').forEach(fill); }, 200);
      });
    }
  }

  /* --- Init --------------------------------------------------------------- */
  function init() {
    initProductForms();
    initGalleries();
    initCardSwaps();
    initStickyHeader();
    initCountdowns();
    initLoadMore();
    initTicker();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-init after Shopify theme-editor section loads
  document.addEventListener('shopify:section:load', init);

  // Expose for other scripts
  window.oe = { qs: qs, qsa: qsa, Overlay: Overlay, Cart: Cart, init: init, bindSwipe: bindSwipe };
})();
